import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { ShapeIcon } from "@/components/ShapeIcon";

type Opt = { value: string; count: number; image?: string };
const tid = (v: string) => v.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\W+/g, "-").replace(/^-|-$/g, "").toLowerCase();

function Explainer({ text, label, open, setOpen, testId, href }: any) {
  if (!text) return null;
  return (
    <>
      <span role="button" tabIndex={0} aria-label={`What is ${label}?`} onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setOpen(!open); } }}
        className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-white/90 text-neutral-700 flex items-center justify-center shadow-sm hover:bg-white" data-testid={`${testId}-info`}><Info size={14} /></span>
      <span onClick={(e) => { if (open) { e.stopPropagation(); setOpen(false); } }} className={`absolute inset-x-0 bottom-0 z-[5] bg-white/95 backdrop-blur-md p-3.5 text-left transition-[opacity,transform] duration-300 ${open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none sm:group-hover:opacity-100 sm:group-hover:translate-y-0"}`} data-testid={`${testId}-explainer`}>
        <span className="block font-display text-base text-neutral-900">What is {label}?</span>
        <span className="block text-[11px] leading-snug text-neutral-600 mt-1">{text}</span>
        {href && <Link to={href} onClick={(e) => e.stopPropagation()} className="inline-block mt-1.5 text-[10px] uppercase tracking-wider underline" data-testid={`${testId}-page-link`}>Explore {label}</Link>}
      </span>
    </>
  );
}

function Tile({ on, onClick, label, testId, children, wide, small, explainer, href }: any) {
  const [open, setOpen] = useState(false);
  return (
    <div role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => e.key === "Enter" && onClick()} onMouseLeave={() => setOpen(false)} data-testid={testId} aria-pressed={on}
      className={`group shrink-0 snap-start text-left cursor-pointer ${wide ? (small ? "w-[29vw] sm:w-[140px]" : "w-[42vw] sm:w-[200px]") : "w-[26vw] sm:w-[120px]"}`}>
      <div className={`relative bg-[#F1EFEA] overflow-hidden ${wide ? "aspect-[5/4]" : "aspect-square"} flex items-center justify-center`}>
        {children}
        <Explainer text={explainer} label={label} open={open} setOpen={setOpen} testId={testId} href={href} />
        <span className={`absolute left-0 right-0 bottom-0 z-[6] h-[3px] bg-neutral-900 origin-left transition-transform duration-300 ${on ? "scale-x-100" : "scale-x-0 group-hover:scale-x-50"}`} />
      </div>
      <p className={`${small ? "mt-2 text-xs sm:text-sm" : "mt-3 text-sm"} ${on ? "text-neutral-900 font-medium" : "text-neutral-600 group-hover:text-neutral-900"} transition-colors`}>{label}</p>
    </div>
  );
}

function Row({ title, children, testId }: any) {
  const ref = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ l: false, r: false });
  const check = () => { const el = ref.current; if (!el) return; const l = el.scrollLeft > 4, r = el.scrollLeft + el.clientWidth < el.scrollWidth - 4; setEdge((e) => (e.l === l && e.r === r ? e : { l, r })); };
  useEffect(() => { check(); window.addEventListener("resize", check); return () => window.removeEventListener("resize", check); }, [children]);
  const go = (d: number) => ref.current?.scrollBy({ left: d * ref.current.clientWidth * 0.8, behavior: "smooth" });
  const Arrow = ({ d, show }: any) => (
    <button type="button" aria-label={d < 0 ? "Scroll left" : "Scroll right"} onClick={() => go(d)} data-testid={`${testId}-${d < 0 ? "prev" : "next"}`}
      className={`hidden md:flex absolute top-[38%] -translate-y-1/2 ${d < 0 ? "left-3" : "right-3"} z-10 w-10 h-10 rounded-full bg-white shadow-md items-center justify-center transition-opacity ${show ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
      {d < 0 ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}</button>
  );
  return (
    <div data-testid={testId}>
      <p className="eyebrow text-[10px] text-neutral-500 mb-4 px-5 md:px-12 max-w-[1400px] mx-auto">{title}</p>
      <div className="relative max-w-[1400px] mx-auto">
        <Arrow d={-1} show={edge.l} />
        <div ref={ref} onScroll={check} className="flex gap-1.5 overflow-x-auto snap-x scroll-px-5 md:scroll-px-12 px-5 md:px-12 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">{children}</div>
        <Arrow d={1} show={edge.r} />
        {edge.r && <span className="hidden md:block pointer-events-none absolute right-0 top-0 bottom-8 w-24 bg-gradient-to-l from-[var(--c-bg)] to-transparent" />}
      </div>
    </div>
  );
}

export function TypeStrip({ config, facets, params, toggle, clear }: { config: any; facets: any; params: URLSearchParams; toggle: (k: string, v: string) => void; clear: () => void }) {
  const param = config.param;
  const counts: Record<string, Opt> = Object.fromEntries((facets?.[config.field] || []).map((o: Opt) => [o.value, o]));
  const tiles = config.show_styles === false ? [] : (config.tiles || []);
  const shapes: Opt[] = config.show_shapes ? (facets?.shapes || []).filter((s: Opt) => s.value.length < 12) : [];
  const selStyles = (params.get(param) || "").split(",").filter(Boolean);
  const selShapes = (params.get("shape") || "").split(",").filter(Boolean);
  const small = config.tile_size !== "standard";
  if (!tiles.length && !shapes.length) return null;
  return (
    <section className="space-y-10 mb-14" data-testid="type-strip" data-size={small ? "compact" : "standard"}>
      {tiles.length > 0 && <Row title="Shop by style" testId="type-strip-styles">
        {tiles.map((t: any) => {
          const img = t.image || counts[t.value]?.image;
          return (
            <Tile key={t.value} wide small={small} on={selStyles.includes(t.value)} onClick={() => toggle(param, t.value)} label={t.label} explainer={t.count ? t.explainer : ""} href={`/collections/${config.page}/style/${encodeURIComponent(t.value)}`} testId={`type-style-${tid(t.value)}`}>
              {img ? <img src={img} alt={`${t.label} ring`} loading="lazy" className={`w-full h-full object-cover ${t.image ? "" : "scale-[1.3] group-hover:scale-[1.4]"} transition-transform duration-700`} />
                : <span className="text-center px-4"><span className={`block font-display ${small ? "text-base" : "text-xl"} text-neutral-700`}>{t.label}</span>{!t.count && <span className="block eyebrow text-[9px] text-[var(--c-primary)] mt-2" data-testid={`type-style-${tid(t.value)}-soon`}>Coming soon</span>}</span>}
            </Tile>);
        })}
        <Tile wide small={small} on={!selStyles.length && !selShapes.length} onClick={clear} label="Shop All" testId="type-style-all">
          <span className={`font-display ${small ? "text-lg" : "text-2xl"} text-neutral-700`}>All</span>
        </Tile>
      </Row>}
      {shapes.length > 0 && <Row title="Shop by diamond shape" testId="type-strip-shapes">
        {shapes.map((s) => (
          <Tile key={s.value} on={selShapes.includes(s.value)} onClick={() => toggle("shape", s.value)} label={s.value} testId={`type-shape-${tid(s.value)}`}>
            <span className="text-neutral-800 group-hover:scale-110 transition-transform duration-300"><ShapeIcon shape={s.value} size={44} /></span>
          </Tile>))}
      </Row>}
    </section>
  );
}
